import { FC, useState } from 'react'
import styled from 'styled-components'

import {
  CardLabel,
  DarkGreenText,
  fontSizeXS,
  fontSizeXXS,
  GreenText,
  OrangeText
} from 'Utils/styles/text'
import { displayDate } from 'Utils/helpers/dateUtils'
import { FlexColumn, spacingSM, spacingXS, spacingXXS } from 'Utils/styles/spacing'
import { elfGreen, frostedGlass, zirconGray } from 'Utils/styles/colors'
import { TransactionAccountInfo, TruelayerConnectionInfo } from 'Utils/types/user'
import { useGetAdminAccountDetailsQuery } from 'Hooks/api/JOONAdmin/useGetAdminAccountDetailsQuery'
import { useCheckPlaidTokenStatusQuery } from 'Hooks/api/JOONAdmin/useCheckPlaidTokenStatusQuery'
import { useForceUpdateModeQuery } from 'Hooks/api/JOONAdmin/useForceUpdateModeQuery'
import { useConfirmationModalContext } from 'Hooks/modals/useConfirmationModal'
import { JoonButton } from 'Components/Buttons/StyledButton'

const StyledContainer = styled(FlexColumn)`
  flex: 1;
`

const CardsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacingXS};
  margin-top: ${spacingXS};
`

const AccountCard = styled(FlexColumn)`
  width: 260px;
  border: 1px solid ${zirconGray};
  border-left: 3px solid ${elfGreen};
  border-radius: 6px;
  overflow: hidden;
`

const CardHeader = styled.div`
  padding: ${spacingXS} ${spacingSM};
  background: ${frostedGlass};
  border-bottom: 1px solid ${zirconGray};
`

const CardHeaderText = styled(GreenText)`
  font-size: ${fontSizeXS};
  font-weight: 600;
`

const CardBody = styled(FlexColumn)`
  padding: ${spacingXS} ${spacingSM};
  gap: ${spacingXXS};
`

const AccountName = styled(DarkGreenText)`
  font-weight: 600;
  font-size: ${fontSizeXS};
`

const DetailRow = styled.span`
  font-size: ${fontSizeXXS};
  color: #3e605a;
  line-height: 1.4;
  word-break: break-all;
`

const DetailLabel = styled.span`
  color: #8a9e9b;
`

const StatusBadge = styled.span<{ valid?: boolean }>`
  font-size: ${fontSizeXXS};
  font-weight: 600;
  color: ${({ valid }) => (valid ? elfGreen : '#d32f2f')};
`

const CardActionButton = styled(JoonButton)`
  margin-top: ${spacingXXS};
  align-self: flex-start;
  height: unset;
  padding: ${spacingXXS} ${spacingXS};
  font-size: ${fontSizeXXS};
`

const StatusMessage = styled.span<{ isError?: boolean }>`
  font-size: ${fontSizeXXS};
  margin-top: ${spacingXXS};
  color: ${({ isError }) => (isError ? '#d32f2f' : elfGreen)};
`

type PlaidAccountCardProps = {
  account: TransactionAccountInfo
  userId: number
}

const PlaidAccountCard: FC<PlaidAccountCardProps> = ({ account, userId }) => {
  const checkToken = useCheckPlaidTokenStatusQuery(userId)
  const forceUpdateMode = useForceUpdateModeQuery(userId)
  const { showModal } = useConfirmationModalContext()
  const [tokenCheckResult, setTokenCheckResult] = useState<{
    message: string | null
    errors: string[]
  } | null>(null)

  const handleCheckToken = async () => {
    setTokenCheckResult(null)
    try {
      const result = await checkToken.mutateAsync(account.id)
      setTokenCheckResult({
        message: result.message,
        errors: result.errors
      })
    } catch {
      setTokenCheckResult({
        message: null,
        errors: ['Failed to check token status']
      })
    }
  }

  const handleForceUpdateMode = async () => {
    const confirmed = await showModal(
      'Force update mode?',
      `This marks ${account.accountName || 'this account'} as requiring ` +
        're-authentication, invalidates its Plaid token, and attempts to ' +
        'reset its sync cursor. The user will need to reconnect this account.'
    )

    if (confirmed) {
      forceUpdateMode.mutate(account.id)
    }
  }

  const latestToken = account.plaidTokens[0]

  return (
    <AccountCard>
      <CardHeader>
        <CardHeaderText>{account.institutionName || 'Unknown'} Account (Plaid)</CardHeaderText>
      </CardHeader>
      <CardBody>
        <AccountName>
          {account.accountName} (···· {account.mask})
        </AccountName>
        <DetailRow>
          <DetailLabel>Type: </DetailLabel>
          {account.accountType}/{account.subType}
        </DetailRow>
        <DetailRow>
          <DetailLabel>Account ID: </DetailLabel>
          {account.accountId}
        </DetailRow>
        {account.createdAt && (
          <DetailRow>
            <DetailLabel>Added: </DetailLabel>
            {displayDate(new Date(account.createdAt))}
          </DetailRow>
        )}
        {latestToken && (
          <DetailRow>
            <DetailLabel>Status: </DetailLabel>
            <StatusBadge valid={latestToken.status === 'VALID'}>{latestToken.status}</StatusBadge>
          </DetailRow>
        )}
        {account.plaidTokens.map((token, idx) => (
          <DetailRow key={idx}>
            <DetailLabel>
              Token {account.plaidTokens.length > 1 ? `${idx + 1} ` : ''}ID:{' '}
            </DetailLabel>
            {token.itemId}
          </DetailRow>
        ))}
        {account.authRequired && <OrangeText>Re-auth required</OrangeText>}
        <CardActionButton
          primary
          secondary={false}
          danger={false}
          disabled={checkToken.isLoading}
          size="small"
          rightIcon={false}
          onClick={handleCheckToken}
        >
          {checkToken.isLoading ? 'Checking...' : 'Check Status'}
        </CardActionButton>
        <CardActionButton
          primary={false}
          secondary={false}
          danger
          disabled={forceUpdateMode.isLoading}
          size="small"
          rightIcon={false}
          onClick={handleForceUpdateMode}
        >
          {forceUpdateMode.isLoading ? 'Forcing...' : 'Force update mode'}
        </CardActionButton>
        {tokenCheckResult && (
          <StatusMessage isError={tokenCheckResult.errors.length > 0}>
            {tokenCheckResult.errors.length > 0
              ? tokenCheckResult.errors.join('; ')
              : tokenCheckResult.message ?? 'No issues found'}
          </StatusMessage>
        )}
      </CardBody>
    </AccountCard>
  )
}

type ConnectedAccountsInfoProps = {
  userId: number
}

export const ConnectedAccountsInfo: FC<ConnectedAccountsInfoProps> = ({ userId }) => {
  const { data, isLoading, isError } = useGetAdminAccountDetailsQuery(userId)

  if (isLoading) {
    return (
      <StyledContainer>
        <CardLabel>Connected Accounts</CardLabel>
        <DarkGreenText>Loading...</DarkGreenText>
      </StyledContainer>
    )
  }

  if (isError) {
    return (
      <StyledContainer>
        <CardLabel>Connected Accounts</CardLabel>
        <OrangeText>Failed to load connected accounts</OrangeText>
      </StyledContainer>
    )
  }

  const hasPlaid = (data?.transactionAccounts?.length ?? 0) > 0
  const hasTruelayer = (data?.truelayerConnections?.length ?? 0) > 0

  if (!hasPlaid && !hasTruelayer) {
    return (
      <StyledContainer>
        <CardLabel>Connected Accounts</CardLabel>
        <OrangeText>No connected accounts</OrangeText>
      </StyledContainer>
    )
  }

  return (
    <StyledContainer>
      <CardLabel>Connected Accounts</CardLabel>

      <CardsGrid>
        {data?.transactionAccounts.map((account: TransactionAccountInfo) => (
          <PlaidAccountCard key={account.id} account={account} userId={userId} />
        ))}

        {data?.truelayerConnections.flatMap((connection: TruelayerConnectionInfo) =>
          connection.accounts.map((account) => (
            <AccountCard key={`tl-${connection.id}-${account.accountId}`}>
              <CardHeader>
                <CardHeaderText>
                  {connection.institutionName ?? 'Unknown'} Account (TrueLayer)
                </CardHeaderText>
              </CardHeader>
              <CardBody>
                <AccountName>{account.displayName}</AccountName>
                <DetailRow>
                  <DetailLabel>Type: </DetailLabel>
                  {account.accountType}
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Account ID: </DetailLabel>
                  {account.accountId}
                </DetailRow>
                {connection.renewBy && (
                  <DetailRow>
                    <DetailLabel>Renew by: </DetailLabel>
                    {displayDate(new Date(connection.renewBy))}
                    {connection.expiresAt && (
                      <>
                        {' | '}
                        <DetailLabel>Expires: </DetailLabel>
                        {displayDate(new Date(connection.expiresAt))}
                      </>
                    )}
                  </DetailRow>
                )}
              </CardBody>
            </AccountCard>
          ))
        )}
      </CardsGrid>
    </StyledContainer>
  )
}
